<script lang="ts">
  import { fade } from 'svelte/transition';
  import { swipe, type SwipeCustomEvent, pinch, type PinchCustomEvent } from 'svelte-gestures';

  import QuickAddCard from './quick-add-card.svelte';
  import ShopTheLookCard from './shop-the-look-card.svelte';
  import Drawer from './drawer.svelte';
  import SliderButton from './ui/slider-button.svelte';

  import { drawer } from '$lib/stores/drawer';

  import { t } from '$lib/i18n';
  import { cn } from '$lib/utils';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { snapScrollPosition } from '$lib/actions/snap-scroll-position';

  import type { ShopTheLookStoryblok } from '$types/bloks';
  import { onMount } from 'svelte';
  import QuickAddCardLoader from './quick-add-card-loader.svelte';
  import { getImageAttributes } from '$lib/utils/cms';
  import { getHash } from '$lib/utils/strings';
  import { buildSizes, buildSrcSet } from '$lib/image';
  import { getUUID } from '$lib/utils/uuid';
  import { AddToCartLocations } from '$lib/analytics';
  import { browser } from '$app/environment';

  const CARD_MIN_WIDTH_PERCENTAGE = 90;

  let className: string | undefined = undefined;
  export { className as class };
  export let look: ShopTheLookStoryblok;
  export let name: string;
  export let showInProductsGrid: boolean | undefined = undefined;

  const markers = look.markers.details;
  const label = markers ? $t('shop-the-look.badge', { value: `${markers.length}` }) : null;
  const src = look.markers.image?.src;
  const id = 'shop-the-look' + getUUID();

  let imageMousewheelLastTimeStamp = 0;

  // Product card carousel
  let carousel: HTMLElement;
  let carouselIdx = 0;
  let host: HTMLElement;

  // Flag to hide transitions when Drawer is opened until interacted
  let hasInteracted = false;
  $: if (carouselIdx !== 0) hasInteracted = true;

  const handleCarousel = (to: 'left' | 'right' | number) => {
    const width = carousel.clientWidth * (CARD_MIN_WIDTH_PERCENTAGE / 100);
    if (to === 'left') {
      carousel.scrollTo({
        left: width * (carouselIdx - 1),
        behavior: $reducedMotion ? 'instant' : 'smooth'
      });
    } else if (to === 'right') {
      carousel.scrollTo({
        left: width * (carouselIdx + 1),
        behavior: $reducedMotion ? 'instant' : 'smooth'
      });
    } else if (typeof to === 'number') {
      carousel.scrollTo({
        left: width * to,
        behavior: $reducedMotion ? 'instant' : 'smooth'
      });
    }
  };

  // Image
  let img: HTMLImageElement;
  $: imageAsset = src
    ? {
        id: getHash(src),
        filename: src,
        alt: name,
        name,
        focus: null,
        source: null,
        title: null,
        copyright: null
      }
    : undefined;
  let imgWidth = 0;
  let imgHeight = 0;
  $: imgAspectRatio = imgWidth / imgHeight;

  onMount(() => {
    // Image natural dimensions for layout references
    if (imageAsset) {
      img = new Image();
      img.src = getImageAttributes(imageAsset, { size: [10, 0] }).src;
      img.onload = () => {
        imgWidth = img.naturalWidth;
        imgHeight = img.naturalHeight;
      };
    }
  });

  const getScaleFactor = ({
    currW,
    desiredW,
    currH,
    desiredH
  }: {
    currW: number;
    desiredW: number;
    currH: number;
    desiredH: number;
  }) => {
    return Math.max(Math.max(1, desiredW / currW), Math.max(1, desiredH / currH));
  };

  const handleImageOnWheel = (event: WheelEvent) => {
    if (event.timeStamp - imageMousewheelLastTimeStamp > 1000) {
      imageMousewheelLastTimeStamp = event.timeStamp;
      handleCarousel(event.deltaX > 0 ? 'right' : 'left');
    }
  };

  const handleImageSwipe = (event: SwipeCustomEvent) => {
    handleCarousel(event.detail.direction == 'left' ? 'right' : 'left');
  };

  let zoomScale: number | undefined = undefined;

  const handleImagePinch = (event: PinchCustomEvent) => {
    zoomScale = event.detail.scale;
  };

  const handleImageTouchEnd = () => {
    if (zoomScale) zoomScale = undefined;
  };

  $: {
    if (browser) {
      window.shopTheLookCarouselIdx = carouselIdx;
    }
  }
</script>

{#if label && src && !!markers?.length}
  {@const imageAsset = {
    id: getHash(src),
    filename: src,
    alt: name,
    name,
    focus: null,
    source: null,
    title: null,
    copyright: null
  }}
  <!-- Rendered Card -->
  <!-- image width is always close to 400px -->
  <div class={cn(className, showInProductsGrid && 'h-full')}>
    <ShopTheLookCard
      src={getImageAttributes(imageAsset, { size: [400, 0] }).src}
      alt={name}
      srcset={buildSrcSet({
        loader: ({ width }) => getImageAttributes(imageAsset, { size: [width, 0] }).src,
        widths: [400]
      })}
      sizes="400px"
      badge={{ label }}
      buttonLabel={$t('shop-the-look.cta')}
      class="h-full"
      {showInProductsGrid}
      on:click={() => {
        drawer.open(id, {
          cleanup: () => {
            carouselIdx = 0;
            hasInteracted = false;
          }
        });
      }}
    />
  </div>

  <!-- Drawer -->
  <Drawer {id} title={$t('shop-the-look.drawer-title')}>
    <div class="relative h-full overflow-hidden" bind:this={host} {id}>
      {#if img && host}
        {@const { w, h } = { w: host.clientHeight * imgAspectRatio, h: host.clientHeight }}
        {@const scale =
          zoomScale ??
          getScaleFactor({
            currW:
              ((markers[carouselIdx].x || 0) > 50
                ? 100 - (markers[carouselIdx].x || 0)
                : markers[carouselIdx].x || 0) *
              0.01 *
              w,
            desiredW: host.clientWidth / 2,
            currH:
              ((markers[carouselIdx].y || 0) > 50
                ? 100 - (markers[carouselIdx].y || 0)
                : markers[carouselIdx].y || 0) *
              0.01 *
              h,
            desiredH: host.clientHeight / 2
          })}

        <!-- Main content -->
        <div
          style:transform="scale({scale})"
          class={cn(
            'h-full',
            hasInteracted &&
              'transform-gpu transition-transform duration-500 motion-reduce:transition-none',
            zoomScale && 'duration-0'
          )}
          on:wheel|preventDefault={handleImageOnWheel}
          use:swipe
          on:swipe={handleImageSwipe}
          use:pinch
          on:pinch={handleImagePinch}
          on:touchend={handleImageTouchEnd}
        >
          <div class="absolute isolate">
            {#if img.src}
              <img
                loading="lazy"
                src={getImageAttributes(imageAsset, { size: [480 * 2, 0] }).src}
                srcset={buildSrcSet({
                  loader: ({ width }) => getImageAttributes(imageAsset, { size: [width, 0] }).src,
                  widths: [480 * 2, (w) => w * 2]
                })}
                sizes={buildSizes({
                  default: `${480 * 2}px`, // in the drawer it's 480px wide * 2 to account for zoom
                  lg: '200vw' // < lg it's full width * 2 to account for zoom
                })}
                alt={name}
                class={cn(
                  'block max-w-none max-h-none',
                  hasInteracted && 'transition-transform duration-500 motion-reduce:transition-none'
                )}
                style:width="{w}px"
                style:height="{h}px"
                style:transform="translate3d(
                {host.clientWidth / 2 - (markers[carouselIdx].x || 0) * 0.01 * w}px,
                {host.clientHeight / 2 - (markers[carouselIdx].y || 0) * 0.01 * h}px, 0px)"
                draggable="false"
              />
            {/if}

            <!-- Markers points -->
            {#each markers || [] as marker, i}
              {#if marker}
                <button
                  aria-label={`marker-${i}`}
                  transition:fade={{
                    duration: $reducedMotion ? 0 : 250
                  }}
                  on:click={() => handleCarousel(i)}
                  class={cn(
                    'absolute z-10 p-4 -translate-x-1/2 -translate-y-1/2 scale-75 cursor-pointer',
                    hasInteracted && 'transition-all duration-500 motion-reduce:transition-none'
                  )}
                  style:top="{marker.y || 0}%"
                  style:left="{marker.x || 0}%"
                  style:transform="
                  translate3d(
                  {-22 + host.clientWidth / 2 - (markers[carouselIdx].x || 0) * 0.01 * w}px,
                  {-22 + host.clientHeight / 2 - (markers[carouselIdx].y || 0) * 0.01 * h}px, 0px)
                  scale({i === carouselIdx ? 1 / scale + 0.2 : 1 / scale})
                  "
                >
                  <div
                    class={cn(
                      'box-content h-3 w-3 rounded-full bg-highlight opacity-75 ring-8 ring-primary/32',
                      hasInteracted && 'transition-all duration-500 motion-reduce:transition-none',
                      i === carouselIdx && 'opacity-100'
                    )}
                  />
                </button>
              {/if}
            {/each}
          </div>
        </div>

        <!-- Desktop slider buttons -->
        <SliderButton
          icon="chevron-left-bold"
          class="absolute left-3 top-1/2 hidden -translate-y-1/2 lg:inline-flex"
          disabled={carouselIdx === 0}
          ariaLabel="Slider Button Left"
          on:click={() => handleCarousel('left')}
        />
        <SliderButton
          icon="chevron-right-bold"
          class="absolute right-3 top-1/2 hidden -translate-y-1/2 lg:inline-flex"
          disabled={carouselIdx === markers.length - 1}
          ariaLabel="Slider Button Right"
          on:click={() => handleCarousel('right')}
        />
      {/if}

      <!-- Product Cards -->
      <div class="absolute bottom-3 w-full lg:bottom-0">
        <div
          class="flex w-full snap-x snap-mandatory gap-3 overflow-x-auto pr-3 hide-scrollbar motion-reduce:transition-none"
          bind:this={carousel}
          use:snapScrollPosition={(i) => (carouselIdx = i)}
          style:--card-min-w="{CARD_MIN_WIDTH_PERCENTAGE}%"
          role="region"
        >
          {#each markers as { title }, i}
            {#if title}
              <QuickAddCardLoader handle={title} enabled={false} let:loading let:product let:load>
                <QuickAddCard
                  class={cn(
                    'min-w-[var(--card-min-w)] flex-1 snap-center snap-always rounded-sm backdrop-blur',
                    i === 0 && 'ml-3'
                  )}
                  on:intersect={load}
                  {loading}
                  {product}
                  productHandle={title}
                  addDrawerTargetId={id}
                  location={AddToCartLocations.SHOP_THE_LOOK}
                  emptyStateImage={imageAsset.filename}
                />
              </QuickAddCardLoader>
            {/if}
          {/each}
        </div>

        <!-- Desktop slider controls and indicator -->
        <div class="mt-5 hidden items-center justify-between bg-highlight p-4 lg:flex">
          <SliderButton
            variant="ghost"
            icon="chevron-left-bold"
            disabled={carouselIdx === 0}
            ariaLabel="Slider Button Left"
            on:click={() => handleCarousel('left')}
          />

          <div class="text-sm lining-nums tabular-nums text-primary/80">
            <span>{carouselIdx + 1}</span>
            <span>{$t('shop-the-look.of')}</span>
            <span>{markers.length}</span>
          </div>

          <SliderButton
            variant="ghost"
            icon="chevron-right-bold"
            disabled={carouselIdx === markers.length - 1}
            ariaLabel="Slider Button Right"
            on:click={() => handleCarousel('right')}
          />
        </div>
      </div>
    </div>
  </Drawer>
{/if}
